"use client";

import { useState } from "react";
import { CalendarDays, UserRound } from "lucide-react";
import {
  serviceLocationLabel,
  SERVICE_LOCATIONS,
  type ServiceLocation,
} from "@/modules/shared/serviceLocation";
import type { BookDayView } from "./BookAppointment";
import { BookingFacts } from "./BookingFacts";
import { StatusColorBadge } from "./BookingStatusBadge";
import { STATUS_COLOR, type StatusTone } from "./bookingStatus";
import { MapLink } from "./MapLink";
import { googleMapsSearchHref } from "./maps";
import { Banner } from "./ui";
import { useFormAction, type FormAction } from "./useFormAction";

/**
 * The clinic's booking queue (docs/ux/screens/BusinessBookingsScreen.md),
 * shared by both frontends (R22 T14).
 *
 * **This is where a clinic decides**, so it is the screen where sharing one
 * implementation matters most in this task. Every rule below is the server's
 * and is re-asked there; what the component must not do is let a control exist
 * that the rule forbids.
 *
 * - Approving an **unassigned** request must name a Practitioner (H23), and the
 *   approve command re-validates that the chosen one is free, so the picker
 *   offers everybody active rather than pre-filtering to a guess.
 * - Approval is also where the clinic confirms or changes the Service Location
 *   (H24). An At-Home approval with no address is refused, here and again on
 *   the server.
 * - A suggested time is a **live offer** (H49): one at a time, withdrawable,
 *   and the customer answers it.
 * - Practitioner-tier members read this queue and act on nothing (H26).
 *
 * The three commands arrive as props. The approve/decline/cancel trio share one
 * form and dispatch on the submitting button's `intent`, which is exactly the
 * submitter-name case `useFormAction` restores.
 */
export interface BookingQueueCommands {
  /** Dispatches on the `intent` field: approve, decline, or cancel. */
  decide: FormAction;
  suggest: FormAction;
  withdrawSuggestion: FormAction;
}

export interface BusinessBookingView {
  id: string;
  /** Null while an unassigned request is open (H23). */
  practitionerId: string | null;
  serviceName: string;
  consumerName: string;
  /** Null while unassigned (H23), shown as "To be assigned". */
  practitionerName: string | null;
  whenLabel: string;
  priceDisplay: string;
  /** Service Location (H24): where the booking is delivered. */
  serviceLocation: ServiceLocation;
  /** "In-Clinic" / "At-Home" (H24). */
  locationLabel: string;
  /** At-Home delivery address (H24); null for In-Clinic. */
  homeAddress: string | null;
  statusLabel: string;
  statusTone: StatusTone;
  /** Effective Requested, approve / decline available. */
  isRequest: boolean;
  /** Effective Approved: suggest a new time / cancel available. */
  isApproved: boolean;
  /**
   * A time this clinic has suggested and the customer has not answered (H49),
   * preformatted. Null when none is live.
   */
  suggestedWhenLabel: string | null;
  /** Unassigned "Any practitioner" request (H23), approval assigns one. */
  isUnassigned: boolean;
}

export interface AssignablePractitioner {
  id: string;
  displayName: string;
}

export function BusinessBookings({
  bookings,
  moveTargets,
  assignable,
  canAct = true,
  viewerPractitionerId = null,
  commands,
}: {
  bookings: BusinessBookingView[];
  moveTargets: Record<string, BookDayView[]>;
  assignable: AssignablePractitioner[];
  commands: BookingQueueCommands;
  /**
   * Whether the caller may act on bookings (H26). Managers = true; a
   * practitioner-tier member views the queue read-only (approve/decline/move/
   * cancel are hidden and, if forged, rejected server-side by requireManager).
   */
  canAct?: boolean;
  /**
   * The practitioner profile the viewer is, when they are one. Used only to
   * decide whether a customer's home address is theirs to see.
   *
   * A home address is shown to the clinic's **managers** and to the
   * **practitioner actually going**, and to nobody else. It fails closed: if
   * this is not supplied, a practitioner-tier viewer sees the area rather than
   * the street. The page must pass it for the assigned practitioner to see the
   * full address.
   */
  viewerPractitionerId?: string | null;
}) {
  const requests = bookings.filter((b) => b.isRequest);
  const upcoming = bookings.filter((b) => b.isApproved);
  const past = bookings.filter((b) => !b.isRequest && !b.isApproved);

  if (bookings.length === 0) {
    return (
      <p className="rounded-xl border border-line bg-surface p-8 text-center text-sm text-muted">
        No bookings yet. They&apos;ll appear here as customers request times.
      </p>
    );
  }

  const row = (booking: BusinessBookingView) => (
    <BookingRow
      key={booking.id}
      booking={booking}
      moveTargets={moveTargets}
      assignable={assignable}
      canAct={canAct}
      viewerPractitionerId={viewerPractitionerId}
      commands={commands}
    />
  );

  return (
    <div className="space-y-8">
      <Section title="Requests to review" empty="No requests waiting." items={requests}>
        {row}
      </Section>
      <Section title="Upcoming" empty="Nothing confirmed yet." items={upcoming}>
        {row}
      </Section>
      {past.length > 0 && (
        <Section title="Past & closed" empty="" items={past}>
          {row}
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  empty,
  items,
  children,
}: {
  title: string;
  empty: string;
  items: BusinessBookingView[];
  children: (booking: BusinessBookingView) => React.ReactNode;
}) {
  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted">
        {title}
      </h2>
      {items.length === 0 ? (
        empty ? (
          <p className="text-sm text-muted">{empty}</p>
        ) : null
      ) : (
        <ul className="space-y-3">{items.map((booking) => children(booking))}</ul>
      )}
    </div>
  );
}

function BookingRow({
  booking,
  moveTargets,
  assignable,
  canAct,
  viewerPractitionerId,
  commands,
}: {
  booking: BusinessBookingView;
  moveTargets: Record<string, BookDayView[]>;
  assignable: AssignablePractitioner[];
  canAct: boolean;
  viewerPractitionerId: string | null;
  commands: BookingQueueCommands;
}) {
  const [moving, setMoving] = useState(false);
  const [assignee, setAssignee] = useState(assignable[0]?.id ?? "");
  // Service Location at approval (H24): the clinic confirms or changes it. Seeded
  // from the request; posting nothing else confirms it as-is.
  const [location, setLocation] = useState<ServiceLocation>(booking.serviceLocation);
  const [address, setAddress] = useState(booking.homeAddress ?? "");
  const atHome = location === "at_home";
  const addressMissing = atHome && address.trim() === "";
  const decide = useFormAction(commands.decide);
  const move = useFormAction(async (form) => {
    const result = await commands.suggest(form);
    if (result.success) setMoving(false);
    return result;
  });
  const withdraw = useFormAction(commands.withdrawSuggestion);
  const error =
    decide.state.formError ??
    move.state.formError ??
    withdraw.state.formError;
  const targets = booking.practitionerId
    ? moveTargets[booking.practitionerId] ?? []
    : [];

  return (
    <li
      className={`rounded-xl border border-line border-l-4 bg-surface p-5 shadow-sm ${STATUS_COLOR[booking.statusTone].stripe}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-ink">{booking.consumerName}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-sm">
            <span className="font-medium text-accent">{booking.serviceName}</span>
            <span className="text-muted">·</span>
            <UserRound className="size-3.5 text-teal" aria-hidden />
            {booking.practitionerName ? (
              <span className="text-body">{booking.practitionerName}</span>
            ) : (
              <span className="italic text-muted">Any practitioner</span>
            )}
          </p>
          <p className="mt-2 inline-flex items-center gap-2 rounded-lg bg-accent/10 px-2.5 py-1 text-sm font-semibold text-ink">
            <CalendarDays className="size-4 text-accent" aria-hidden />
            {booking.whenLabel}
          </p>
          <BookingFacts
            priceDisplay={booking.priceDisplay}
            locationLabel={booking.locationLabel}
            atHome={booking.serviceLocation === "at_home"}
          />
          {booking.homeAddress &&
            (canAct || viewerPractitionerId === booking.practitionerId ? (
              <p className="mt-1 text-sm">
                <MapLink href={googleMapsSearchHref(booking.homeAddress)}>
                  {booking.homeAddress}
                </MapLink>
              </p>
            ) : (
              /* Not this practitioner's visit: the address is not theirs to read.
                 Fails closed, so an unwired viewerPractitionerId hides rather
                 than leaks. */
              <p className="mt-1 text-sm text-muted">
                Address shown to the practitioner going
              </p>
            ))}
        </div>
        <StatusColorBadge tone={booking.statusTone} label={booking.statusLabel} />
      </div>

      {error && (
        <div className="mt-3">
          <Banner>{error}</Banner>
        </div>
      )}

      {/* Service Location at approval (H24): confirm or change it. The command
          validates the final location against the assigned practitioner.
          Hidden for view-only practitioners (H26). */}
      {canAct && booking.isRequest && (
        <div className="mt-3 border-t border-line pt-3">
          <label className="text-sm">
            <span className="mb-1 block font-medium text-ink">Service location</span>
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value as ServiceLocation)}
              className="rounded-lg border border-line bg-surface px-3 py-2 text-body outline-none focus:border-accent"
            >
              {SERVICE_LOCATIONS.map((loc) => (
                <option key={loc} value={loc}>
                  {serviceLocationLabel(loc)}
                </option>
              ))}
            </select>
          </label>
          {atHome && (
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Customer's address for the home visit"
              className="mt-2 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-body outline-none focus:border-accent"
            />
          )}
        </div>
      )}

      {/* Unassigned "Any practitioner" request (H23): pick who to assign, then
          approve. The command re-validates the choice against availability.
          Hidden for view-only practitioners (H26). */}
      {canAct && booking.isUnassigned && (
        <div className="mt-3 border-t border-line pt-3">
          {assignable.length === 0 ? (
            <p className="text-sm text-muted">
              Add an active practitioner before you can approve this request.
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm">
                <span className="mb-1 block font-medium text-ink">
                  Assign practitioner
                </span>
                <select
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  className="rounded-lg border border-line bg-surface px-3 py-2 text-body outline-none focus:border-accent"
                >
                  {assignable.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
              </label>
              <form onSubmit={decide.onSubmit}>
                <input type="hidden" name="bookingId" value={booking.id} />
                <input type="hidden" name="intent" value="approve" />
                <input type="hidden" name="practitionerId" value={assignee} />
                <input type="hidden" name="serviceLocation" value={location} />
                {atHome && <input type="hidden" name="homeAddress" value={address} />}
                <ActionButton
                  label="Approve"
                  pending="Approving…"
                  tone="primary"
                  disabled={addressMissing}
                  busy={decide.pending}
                />
              </form>
              <form onSubmit={decide.onSubmit}>
                <input type="hidden" name="bookingId" value={booking.id} />
                <input type="hidden" name="intent" value="decline" />
                <ActionButton
                  label="Decline"
                  pending="Declining…"
                  tone="danger"
                  busy={decide.pending}
                />
              </form>
            </div>
          )}
        </div>
      )}

      {canAct && ((booking.isRequest && !booking.isUnassigned) || booking.isApproved) && (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
          {booking.isRequest && (
            <form onSubmit={decide.onSubmit}>
              <input type="hidden" name="bookingId" value={booking.id} />
              <input type="hidden" name="intent" value="approve" />
              <input type="hidden" name="serviceLocation" value={location} />
              {atHome && <input type="hidden" name="homeAddress" value={address} />}
              <ActionButton
                label="Approve"
                pending="Approving…"
                tone="primary"
                disabled={addressMissing}
                busy={decide.pending}
              />
            </form>
          )}
          {booking.isRequest && (
            <form onSubmit={decide.onSubmit}>
              <input type="hidden" name="bookingId" value={booking.id} />
              <input type="hidden" name="intent" value="decline" />
              <ActionButton
                label="Decline"
                pending="Declining…"
                tone="danger"
                busy={decide.pending}
              />
            </form>
          )}
          {/* H49: the clinic no longer moves a booking over the customer's
              head. It offers a time, and the customer confirms or rejects. */}
          <button
            type="button"
            onClick={() => setMoving((m) => !m)}
            className="rounded-lg border border-line px-3 py-1.5 text-sm font-semibold text-body hover:border-accent"
          >
            {moving ? "Cancel" : "Suggest a new time"}
          </button>
          {booking.isApproved && (
            <form onSubmit={decide.onSubmit}>
              <input type="hidden" name="bookingId" value={booking.id} />
              <input type="hidden" name="intent" value="cancel" />
              <ActionButton
                label="Cancel booking"
                pending="Cancelling…"
                tone="danger"
                busy={decide.pending}
              />
            </form>
          )}
        </div>
      )}

      {/* A suggestion this clinic is waiting on (H49). It reserves nothing, so
          the copy says so: the hour can still go to somebody else while the
          customer thinks about it. */}
      {booking.suggestedWhenLabel && (
        <div className="mt-3 rounded-lg border border-accent/40 bg-accent/5 p-3">
          <p className="text-sm font-semibold text-ink">
            Suggested {booking.suggestedWhenLabel}, waiting on the customer
          </p>
          <p className="mt-0.5 text-xs text-muted">
            The booking stays at {booking.whenLabel} until they answer, and the
            suggested hour is not held for them.
          </p>
          {canAct && (
            <form onSubmit={withdraw.onSubmit} className="mt-2">
              <input type="hidden" name="bookingId" value={booking.id} />
              <ActionButton
                label="Withdraw suggestion"
                pending="Withdrawing…"
                tone="danger"
                busy={withdraw.pending}
              />
            </form>
          )}
        </div>
      )}

      {canAct && moving && (
        <div className="mt-3 rounded-lg border border-line bg-page p-4">
          <p className="mb-2 text-sm font-medium text-ink">
            Suggest another open time for {booking.practitionerName}:
          </p>
          {targets.length === 0 ? (
            <p className="text-sm text-muted">
              No other open times in this practitioner&apos;s next 7 days.
            </p>
          ) : (
            <div className="space-y-3">
              {targets.map((day) => (
                <div key={day.date}>
                  <p className="mb-1 text-xs font-semibold text-muted">{day.dateLabel}</p>
                  <div className="flex flex-wrap gap-2">
                    {day.blocks.map((block) => (
                      <form
                        onSubmit={move.onSubmit}
                        key={`${block.startMinutes}-${block.endMinutes}`}
                      >
                        <input type="hidden" name="bookingId" value={booking.id} />
                        <input type="hidden" name="date" value={day.date} />
                        <input type="hidden" name="startMinutes" value={block.startMinutes} />
                        <input type="hidden" name="endMinutes" value={block.endMinutes} />
                        <SuggestButton
                          label={block.timeLabel}
                          busy={move.pending}
                        />
                      </form>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function ActionButton({
  label,
  pending,
  tone,
  disabled,
  busy,
}: {
  label: string;
  pending: string;
  tone: "primary" | "danger";
  disabled?: boolean;
  /** Supplied by the caller: there is no form action to read it from. */
  busy: boolean;
}) {
  const cls =
    tone === "primary"
      ? "bg-primary text-on-primary hover:brightness-110"
      : "border border-danger/40 text-danger hover:bg-danger/10";
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className={`rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-60 ${cls}`}
    >
      {busy ? pending : label}
    </button>
  );
}

function SuggestButton({ label, busy }: { label: string; busy: boolean }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="rounded-lg border border-accent bg-accent/5 px-3 py-1 text-sm font-medium text-accent transition-colors hover:bg-accent hover:text-on-accent disabled:opacity-60"
    >
      {busy ? "Suggesting…" : label}
    </button>
  );
}
